import React, { ComponentClass, ReactNode, WeakValidationMap } from 'react';

import { SingleSourceOfTruth } from '@philips-emr/tws-core';
import { NotifyData } from '@philips-emr/tws-core/lib/single-source-of-truth';
import { isEmptyObject, noop } from '@philips-emr/tws-utils';

import { EngineStuff } from '../../components/engine-stuff';
import EngineContext from '../../context/engine-context';
import ComponentNode from '../../core/component-node';
import FragmentNode from '../../core/fragment-node';
import { EngineProps } from '../../renderer/react-renderer';

import PropsManager from './props-manager';

/**
 * A real instance of SSOT is going to be provided in PropsManager,
 * this one is necessary to TS works properly.
 */
const DUMMY_SSOT = new SingleSourceOfTruth('', {}, []);

export default function ClassBridge(name: string, StatefulComponentType: ComponentClass<EngineProps>): ComponentClass<EngineProps> {

  return class ClassBridgeComponent extends React.Component<EngineProps> {

    static contextType = EngineContext;

    // @ts-ignore
    static get name(): string {
      return name;
    }

    static get propTypes(): WeakValidationMap<EngineProps> {
      return StatefulComponentType.propTypes as WeakValidationMap<EngineProps>;
    }

    context!: React.ContextType<typeof EngineContext>;
    unsubscribe: Function = noop;

    componentDidMount(): void {
      const component = this.getEngineComponent();

      const ssot = component.getSSOT();

      this.unsubscribe = ssot.subscribe(this.listenSSOT);
    }

    listenSSOT = ({ props }: NotifyData): void => {
      if (!isEmptyObject(props)) {
        this.forceUpdate();
      }
    };

    componentWillUnmount(): void {
      this.unsubscribe && this.unsubscribe();
      this.getParent().discard(this.props.uuid, this.getInstanceUuid());
    }

    getEngineComponent(): ComponentNode {
      return this.getParent().provideNode(this.props.uuid, this.getInstanceUuid());
    }

    getParent(): FragmentNode {
      const parent = this.context.parent;
      if (!parent) throw new Error();
      return parent;
    }

    getInstanceUuid(): string {
      let identifier = this.props.uuid;

      if (this.props[`@repeated`]) {
        identifier = this.props.identifier || '';
      }

      return this.context.parentInstanceUUID + identifier;
    }

    childDidMount = (): void => {
      const node = this.getEngineComponent();
      const eventDelegator = node.getEventDelegator();
      eventDelegator.fire('onMount');
    };

    childWillUnmount = (): void => {
      const node = this.getEngineComponent();
      const eventDelegator = node.getEventDelegator();
      eventDelegator.fire('onUnmount');
    };

    render(): ReactNode {
      const { uuid, children, ssot, onMount, onUnmount, ...otherProps } = this.props; //eslint-disable-line
      const component = this.getEngineComponent();

      const engineStuff: EngineStuff = {
        ...this.context,
        parentInstanceUUID: this.getInstanceUuid(),
      };

      return (
        <EngineContext.Provider value={engineStuff}>
          <PropsManager engineComponent={component} {...otherProps} >
            <StatefulComponentType uuid={uuid} onMount={this.childDidMount} onUnmount={this.childWillUnmount} ssot={DUMMY_SSOT} >
              {children}
            </StatefulComponentType>
          </PropsManager>
        </EngineContext.Provider>
      );
    }
  };
}
